import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import type { DateEventColor } from "@bedrock/domain";
import { Button, Icon, IconButton } from "@bedrock/ui";

interface PickerPosition {
  left: number;
  top: number;
  placement: "above" | "below";
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function PickerPopover({ anchorRef, className, color, id, label, open, onClose, children }: {
  anchorRef: { current: HTMLButtonElement | null };
  className: string;
  color: DateEventColor;
  id: string;
  label: string;
  open: boolean;
  onClose: (restoreFocus?: boolean) => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PickerPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const anchorBounds = anchor.getBoundingClientRect();
      const panelBounds = panel.getBoundingClientRect();
      const margin = 12;
      const gap = 9;
      const roomBelow = window.innerHeight - anchorBounds.bottom - margin;
      const roomAbove = anchorBounds.top - margin;
      const placement = roomBelow >= panelBounds.height + gap || roomBelow >= roomAbove ? "below" : "above";
      const top = placement === "below" ? anchorBounds.bottom + gap : anchorBounds.top - panelBounds.height - gap;
      setPosition({
        left: clamp(anchorBounds.left, margin, Math.max(margin, window.innerWidth - panelBounds.width - margin)),
        top: clamp(top, margin, Math.max(margin, window.innerHeight - panelBounds.height - margin)),
        placement
      });
    };

    place();
    window.addEventListener("resize", place);
    document.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !anchorRef.current?.contains(target)) onClose(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
    };
    window.addEventListener("pointerdown", closeOnPointerDown, { capture: true });
    window.addEventListener("keydown", closeOnEscape, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", closeOnPointerDown, { capture: true });
      window.removeEventListener("keydown", closeOnEscape, { capture: true });
    };
  }, [anchorRef, onClose, open]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label={label}
      data-modal-escape-boundary="true"
      data-placement={position?.placement ?? "below"}
      className={`br-floating-panel date-time-picker-popover ${className} date-event-color--${color}`}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden"
      }}
    >
      {children}
    </div>,
    document.body
  );
}

function parseDateValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day, 12);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

function dateValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 12);
}

function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount, 12);
}

function moveMonth(date: Date, amount: number): Date {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + amount + 1, 0, 12).getDate();
  return new Date(date.getFullYear(), date.getMonth() + amount, Math.min(date.getDate(), lastDay), 12);
}

function calendarLabel(date: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(date);
}

function dateTriggerParts(date: Date, locale?: string): { primary: string; secondary: string } {
  return {
    primary: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(date),
    secondary: new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date)
  };
}

export function DatePicker({ value, color, disabled = false, locale, onChange }: {
  value: string;
  color: DateEventColor;
  disabled?: boolean;
  locale?: string;
  onChange: (value: string) => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerId = useId();
  const selected = parseDateValue(value) ?? new Date();
  const selectedValue = dateValue(selected);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected));
  const [focusedDate, setFocusedDate] = useState(selectedValue);
  const triggerParts = dateTriggerParts(selected, locale);
  const todayValue = dateValue(new Date());

  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) queueMicrotask(() => triggerRef.current?.focus());
  };
  const openPicker = () => {
    setViewMonth(startOfMonth(selected));
    setFocusedDate(selectedValue);
    setOpen(true);
  };
  const selectDate = (next: Date) => {
    onChange(dateValue(next));
    close(true);
  };

  useEffect(() => {
    if (!open) return;
    document.getElementById(pickerId)?.querySelector<HTMLElement>(`[data-calendar-date="${focusedDate}"]`)?.focus();
  }, [focusedDate, open, pickerId, viewMonth]);

  const gridStart = addDays(viewMonth, -viewMonth.getDay());
  const weeks = Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => addDays(gridStart, week * 7 + day)));
  const weekdayBase = new Date(2024, 0, 7, 12);
  const weekdays = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekdayBase, index);
    return {
      short: new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(date),
      long: new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date)
    };
  });

  const focusCalendarDate = (next: Date) => {
    setViewMonth(startOfMonth(next));
    setFocusedDate(dateValue(next));
  };
  const handleDayKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, date: Date) => {
    let next: Date | null = null;
    if (event.key === "ArrowLeft") next = addDays(date, -1);
    if (event.key === "ArrowRight") next = addDays(date, 1);
    if (event.key === "ArrowUp") next = addDays(date, -7);
    if (event.key === "ArrowDown") next = addDays(date, 7);
    if (event.key === "Home") next = addDays(date, -date.getDay());
    if (event.key === "End") next = addDays(date, 6 - date.getDay());
    if (event.key === "PageUp") next = moveMonth(date, -1);
    if (event.key === "PageDown") next = moveMonth(date, 1);
    if (!next) return;
    event.preventDefault();
    focusCalendarDate(next);
  };

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={`date-time-picker-trigger date-event-color--${color}`}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? pickerId : undefined}
      aria-label={`Choose date, ${calendarLabel(selected, locale)}`}
      onClick={() => open ? close() : openPicker()}
    >
      <span className="date-time-picker-trigger__icon"><Icon name="calendar"/></span>
      <span className="date-time-picker-trigger__copy"><strong>{triggerParts.primary}</strong><small>{triggerParts.secondary}</small></span>
      <span className="date-time-picker-trigger__marker" aria-hidden="true"/>
    </button>
    <PickerPopover anchorRef={triggerRef} className="date-picker-popover" color={color} id={pickerId} label="Choose date" open={open} onClose={close}>
      <header className="date-time-picker-popover__header">
        <div><span>Choose a date</span><strong>{new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(viewMonth)}</strong></div>
        <nav aria-label="Change month">
          <IconButton type="button" label="Previous month" icon="arrow-left" tooltip={false} onClick={() => setViewMonth((current) => startOfMonth(moveMonth(current, -1)))}/>
          <IconButton type="button" label="Next month" icon="arrow-right" tooltip={false} onClick={() => setViewMonth((current) => startOfMonth(moveMonth(current, 1)))}/>
        </nav>
      </header>
      <div className="date-picker__grid" role="grid" aria-label={new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(viewMonth)}>
        <div className="date-picker__weekdays" role="row">
          {weekdays.map((weekday) => <span key={weekday.long} role="columnheader" aria-label={weekday.long}>{weekday.short}</span>)}
        </div>
        {weeks.map((week) => <div key={dateValue(week[0]!)} className="date-picker__week" role="row">
          {week.map((date) => {
            const nextValue = dateValue(date);
            const selectedDay = nextValue === selectedValue;
            return <button
              key={nextValue}
              type="button"
              role="gridcell"
              tabIndex={nextValue === focusedDate ? 0 : -1}
              aria-label={calendarLabel(date, locale)}
              aria-selected={selectedDay}
              aria-current={nextValue === todayValue ? "date" : undefined}
              data-calendar-date={nextValue}
              data-outside-month={date.getMonth() !== viewMonth.getMonth() ? "true" : "false"}
              className={selectedDay ? "is-selected" : ""}
              onFocus={() => setFocusedDate(nextValue)}
              onKeyDown={(event) => handleDayKeyDown(event, date)}
              onClick={() => selectDate(date)}
            >
              <span>{date.getDate()}</span>
            </button>;
          })}
        </div>)}
      </div>
      <footer className="date-time-picker-popover__footer">
        <span>{calendarLabel(selected, locale)}</span>
        <Button type="button" onClick={() => selectDate(parseDateValue(todayValue)!)}>Today</Button>
      </footer>
    </PickerPopover>
  </>;
}

function parseTimeValue(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour <= 23 && minute <= 59 ? { hour, minute } : null;
}

function padTime(value: number): string {
  return String(value).padStart(2, "0");
}

function formatTime(value: string, locale?: string): string {
  const parsed = parseTimeValue(value);
  if (!parsed) return "No time";
  const { hour, minute } = parsed;
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(2000, 0, 1, hour, minute));
}

function suggestedTime(): { hour: number; minute: number } {
  const nextSlot = new Date();
  nextSlot.setSeconds(0, 0);
  nextSlot.setMinutes((Math.floor(nextSlot.getMinutes() / 30) + 1) * 30);
  return { hour: nextSlot.getHours(), minute: nextSlot.getMinutes() };
}

function TimeColumn({ part, label, values, selected, onSelect }: {
  part: "hour" | "minute";
  label: string;
  values: number[];
  selected: number;
  onSelect: (value: number) => void;
}) {
  const focusValue = (value: number) => {
    onSelect(value);
    queueMicrotask(() => document.querySelector<HTMLElement>(`[data-time-part="${part}"][data-time-value="${padTime(value)}"]`)?.focus());
  };
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, value: number) => {
    let next: number | null = null;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") next = (value + 1) % values.length;
    if (event.key === "ArrowUp" || event.key === "ArrowLeft") next = (value - 1 + values.length) % values.length;
    if (event.key === "PageDown") next = Math.min(values.length - 1, value + 5);
    if (event.key === "PageUp") next = Math.max(0, value - 5);
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = values.length - 1;
    if (next === null) return;
    event.preventDefault();
    focusValue(next);
  };

  return <div className="time-picker__column">
    <span>{label}</span>
    <div role="listbox" aria-label={label}>
      {values.map((value) => <button
        key={value}
        type="button"
        role="option"
        tabIndex={value === selected ? 0 : -1}
        aria-selected={value === selected}
        aria-label={`${padTime(value)} ${part === "hour" ? "hours" : "minutes"}`}
        className={value === selected ? "is-selected" : ""}
        data-time-part={part}
        data-time-value={padTime(value)}
        onKeyDown={(event) => handleKeyDown(event, value)}
        onClick={() => focusValue(value)}
      >{padTime(value)}</button>)}
    </div>
  </div>;
}

export function TimePicker({ value, color, disabled = false, locale, onChange }: {
  value: string;
  color: DateEventColor;
  disabled?: boolean;
  locale?: string;
  onChange: (value: string) => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerId = useId();
  const [open, setOpen] = useState(false);
  const [fallbackTime] = useState(suggestedTime);
  const parsedTime = parseTimeValue(value);
  const { hour, minute } = parsedTime ?? fallbackTime;
  const hasTime = parsedTime !== null;
  const hours = Array.from({ length: 24 }, (_, index) => index);
  const minutes = Array.from({ length: 60 }, (_, index) => index);
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) queueMicrotask(() => triggerRef.current?.focus());
  };
  const setTimePart = (part: "hour" | "minute", next: number) => {
    onChange(`${padTime(part === "hour" ? next : hour)}:${padTime(part === "minute" ? next : minute)}`);
  };

  useEffect(() => {
    if (!open) return;
    const panel = document.getElementById(pickerId);
    const selectedHour = panel?.querySelector<HTMLElement>(`[data-time-part="hour"][data-time-value="${padTime(hour)}"]`);
    const selectedMinute = panel?.querySelector<HTMLElement>(`[data-time-part="minute"][data-time-value="${padTime(minute)}"]`);
    if (selectedHour?.parentElement) selectedHour.parentElement.scrollTop = hour * 38;
    if (selectedMinute?.parentElement) selectedMinute.parentElement.scrollTop = minute * 38;
    selectedHour?.focus({ preventScroll: true });
  }, [hour, minute, open, pickerId]);

  const chooseNow = () => {
    const now = new Date();
    onChange(`${padTime(now.getHours())}:${padTime(now.getMinutes())}`);
  };
  const clearTime = () => {
    onChange("");
    close(true);
  };

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={`date-time-picker-trigger date-event-color--${color}`}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? pickerId : undefined}
      aria-label={hasTime ? `Choose time, ${formatTime(value, locale)}` : "Choose time, no time set"}
      onClick={() => setOpen((current) => !current)}
    >
      <span className="date-time-picker-trigger__icon"><Icon name="clock"/></span>
      <span className="date-time-picker-trigger__copy"><strong>{formatTime(value, locale)}</strong><small>{hasTime ? "Local time" : "Date only"}</small></span>
      <span className="date-time-picker-trigger__marker" aria-hidden="true"/>
    </button>
    <PickerPopover anchorRef={triggerRef} className="time-picker-popover" color={color} id={pickerId} label="Choose time" open={open} onClose={close}>
      <header className="time-picker__header"><span>Choose a time</span><strong>{formatTime(value, locale)}</strong><small>{hasTime ? "Exact to the minute" : "Keep it date-only or choose a time"}</small></header>
      <div className="time-picker__rails" aria-label="Time controls">
        <TimeColumn part="hour" label="Hour" values={hours} selected={hour} onSelect={(next) => setTimePart("hour", next)}/>
        <span className="time-picker__colon" aria-hidden="true">:</span>
        <TimeColumn part="minute" label="Minute" values={minutes} selected={minute} onSelect={(next) => setTimePart("minute", next)}/>
      </div>
      <footer className="date-time-picker-popover__footer">
        <div className="time-picker__footer-options">
          <Button type="button" aria-pressed={!hasTime} onClick={clearTime}>No time</Button>
          <Button type="button" onClick={chooseNow}>Now</Button>
        </div>
        <Button type="button" tone="primary" onClick={() => close(true)}>Done</Button>
      </footer>
    </PickerPopover>
  </>;
}
